import {
    autoUpdate,
    flip,
    FloatingPortal,
    OpenChangeReason,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
} from '@floating-ui/react'
import { IconLayoutSidebarRight, IconX } from '@tabler/icons-react'
import { Dayjs } from 'dayjs'
import capitalize from 'lodash/capitalize'
import { MouseEvent, ReactNode, useEffect, useState } from 'react'

import { AstraButton, AstraGlassCard, AstraIcon, AstraTooltip, astraTwMerge, format, useDataGridContext } from '~/astra'
import { SidebarSelection } from '~/store/slices/selectionDrawerSlice'
import { Location, useLocation } from '~/storm'

import { Breadcrumbs } from '../BreadcrumbsHeader'
import { CommentIndicator } from './CommentIndicator'
import { CommentList } from './CommentList'
import { isLocationDateSelection } from './utils'

export type CommentPopoverProps = {
    children: ReactNode
    date: Dayjs
    locationId: Location['id']
}

type CommentPopoverContent = {
    onClose: () => void
    autoFocusInput?: boolean
    onOpenInSidebar?: () => void
    selection: SidebarSelection
}
function LocationRoomCodeBreadcrumbs({ selection, locationId }: { selection: SidebarSelection; locationId: number }) {
    const location = useLocation({ where: { id: locationId } })
    const dateCrumb = { label: capitalize(format(selection.date, 'dddd D. MMMM')) }
    const crumbs = location?.room_code ? [dateCrumb, { label: location.room_code }] : [dateCrumb]
    return <Breadcrumbs crumbs={crumbs} />
}

export const CommentPopoverContent = ({ onClose, autoFocusInput = false, onOpenInSidebar, selection }: CommentPopoverContent) => {
    const handleOpenInSidebar = () => {
        onOpenInSidebar?.()
        onClose()
    }
    return (
        <AstraGlassCard>
            <div className="flex h-full max-h-[45vh] w-[400px] flex-col">
                <div className="flex items-center justify-between border-b border-base p-1">
                    {isLocationDateSelection(selection) ? (
                        <LocationRoomCodeBreadcrumbs selection={selection} locationId={selection.locationId} />
                    ) : (
                        <Breadcrumbs crumbs={[{ label: capitalize(format(selection.date, 'dddd D. MMMM')) }]} />
                    )}
                    <div className="flex items-center gap-2">
                        <AstraTooltip tooltipContent="Åpne kommentarer i sidepanel">
                            <AstraButton
                                variant="ghost"
                                onClick={handleOpenInSidebar}
                                icon={<AstraIcon icon={IconLayoutSidebarRight} />}
                                aria-label="Åpne kommentarer i sidepanel"
                            />
                        </AstraTooltip>
                        <AstraButton variant="ghost" icon={<AstraIcon icon={IconX} />} aria-label="Lukk" onClick={onClose} />
                    </div>
                </div>
                <CommentList showFilters={false} showEmpty={false} selection={selection} autoFocusInput={autoFocusInput} showGroupedLocations={false} />
            </div>
        </AstraGlassCard>
    )
}

export type CellCommentPopoverProps = {
    date: Dayjs
    locationId?: Location['id']
    onOpenChange?: (isOpen: boolean, reason: OpenChangeReason | undefined) => void
    commentCount?: number
    className?: string
    onOpenInSidebar?: () => void
    onTriggerClick?: (e: MouseEvent<HTMLDivElement>) => void
    children?: ReactNode
}

export const CellCommentPopover = ({
    date,
    locationId,
    onOpenChange,
    commentCount,
    className,
    onOpenInSidebar,
    onTriggerClick,
    children,
}: CellCommentPopoverProps) => {
    const [{ isOpen, reason }, setIsOpen] = useState<{ isOpen: boolean; reason: OpenChangeReason | undefined }>({ isOpen: false, reason: undefined })
    const [hasClickedInside, setHasClickedInside] = useState(false)
    const selection: SidebarSelection = locationId === undefined ? { type: 'scheduleDate', date } : { type: 'locationDate', date, locationId }
    // the reason for not using useAstraPopover is:
    //   would need to extend to pass/override styling options
    //   we combine hover and clicks, which is trivial using useInteractions
    const { context, refs, floatingStyles } = useFloating({
        open: isOpen,
        onOpenChange: (isOpen, _, reason) => {
            setIsOpen({ isOpen, reason })
            onOpenChange?.(isOpen, reason)
        },
        placement: 'right',

        middleware: [flip(), shift({ padding: 8, crossAxis: true })],
        whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false }),
    })

    const hover = useHover(context, {
        restMs: 450,
        handleClose: safePolygon({
            requireIntent: false,
            buffer: 50,
        }),
        enabled: !hasClickedInside,
    })
    const onClick = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'dialog' })
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, onClick, dismiss, role])

    const { setBlockKeyInput } = useDataGridContext()

    useEffect(() => {
        setBlockKeyInput(isOpen)
        setHasClickedInside(false)

        return () => {
            if (isOpen) {
                setBlockKeyInput(false)
            }
        }
    }, [isOpen, setBlockKeyInput])

    return (
        <div className={astraTwMerge('flex w-full justify-end @container', className)}>
            <div
                className="pointer-events-auto [&_p]:hidden [&_p]:@[65px]:block"
                {...getReferenceProps({
                    onClick: e => {
                        // prevents cell modal from opening
                        e.preventDefault()
                        //  prevents selecting the cell - remove if we want trigger to select the cell
                        e.stopPropagation()
                        onTriggerClick?.(e as MouseEvent<HTMLDivElement>)
                    },
                })}
                ref={node => {
                    refs.setReference(node)
                }}
            >
                {children ?? <CommentIndicator commentCount={commentCount} />}
            </div>
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="z-priority"
                        style={floatingStyles}
                        {...getFloatingProps({
                            onClick: e => {
                                setHasClickedInside(true)
                                // prevent clicks on content to open the cell modal
                                e.stopPropagation()
                            },
                        })}
                    >
                        <CommentPopoverContent
                            selection={selection}
                            onClose={() => setIsOpen({ isOpen: false, reason: 'click' })}
                            autoFocusInput={reason === 'click'}
                            onOpenInSidebar={onOpenInSidebar}
                        />
                    </div>
                </FloatingPortal>
            )}
        </div>
    )
}
